<div class="lm">
	<main class="lm__main">

		<!-- LIST -->
		<ng-container *ngIf="mode === 'list'">
			<div class="lm__pageHeader">
				<div>
					<h1>Pattern Overview</h1>
					<p>Manage, monitor and test your defined log patterns.</p>
				</div>

				<div class="lm__headerActions">
					<button class="lm__btn" type="button" (click)="refresh()" [disabled]="loading$ | async">
						Refresh
					</button>
				</div>
			</div>

			<!-- TEXT FILTER -->
			<div class="lm__filterRow">
				<label class="lm__label" style="flex:1;">
					<span class="lm__labelTitle">Filter</span>
					<input class="lm__input" type="text" [(ngModel)]="q" (ngModelChange)="onFilterChange($event)"
						placeholder="Search title, pattern, category..." />
				</label>
			</div>

			<!-- MATCH TEST AREA -->
			<div class="lm__filterRow">
				<label class="lm__label lm__label--grow" style="flex:1;">
					<span class="lm__labelTitle">
						Test text (show matching patterns)
					</span>
					<textarea class="lm__textarea" rows="4" [(ngModel)]="matchText"
						(ngModelChange)="onMatchTextChange($event)"
						placeholder="Paste a log message here to see which patterns would match..."></textarea>
				</label>

				<label class="lm__label" style="align-self:flex-end;">
					<span class="lm__labelTitle">View</span>
					<div class="lm__viewToggle">
						<input type="checkbox" [checked]="onlyMatches"
							(change)="toggleOnlyMatches($any($event.target).checked)" />
						<span class="lm__muted">Only matches</span>
					</div>
				</label>
			</div>

			<div class="lm__error" *ngIf="(error$ | async) as err">
				<span *ngIf="err">{{ err }}</span>
			</div>

			<!-- LIST CONTENT -->

			<section class="lm__list" *ngIf="pagedPatterns$ | async as patterns">
				<div class="lm__pagerBar" *ngIf="(filteredCount$ | async) as count">
					<div class="lm__pagerInfo">
						<div class="lm__pagerTitle">Results</div>
						<div class="lm__pagerSub">
							{{ count }} result(s) • Page {{ page }} of {{ (totalPages$ | async) }}
						</div>
					</div>

					<div class="lm__pagerControls">
						<label class="lm__pagerSize">
							<span class="lm__pagerSizeLabel">Page size</span>
							<select class="lm__input lm__pagerSizeSelect" [ngModel]="pageSize"
								(ngModelChange)="setPageSize($event)">
								<option *ngFor="let s of pageSizeOptions" [ngValue]="s">{{ s }}</option>
							</select>
						</label>


						<div class="lm__pagerBtns" *ngIf="(totalPages$ | async) as totalPages">
							<button class="lm__pagerBtn" type="button" (click)="setPage(page - 1)" [disabled]="page <= 1">
								Prev
							</button>

							<ng-container *ngIf="pagerTokens$ | async as tokens">
								<ng-container *ngFor="let t of tokens">
									<span *ngIf="t.kind === 'gap'" class="lm__pagerGap">…</span>

									<button *ngIf="t.kind === 'page'" class="lm__pagerBtn lm__pagerBtn--num" [class.lm__pagerBtn--active]="t.isCurrent" type="button" (click)="setPage(t.value)" [attr.aria-current]="t.isCurrent ? 'page' : null">
										{{ t.value }}
									</button>
								</ng-container>
							</ng-container>

							<button class="lm__pagerBtn" type="button" (click)="setPage(page + 1)" [disabled]="page >= totalPages">
								Next
							</button>
						</div>
					</div>
				</div>


				<div class="lm__empty" *ngIf="patterns.length === 0 && !(loading$ | async)">
					No patterns found.
				</div>

				<ng-container *ngIf="applications$ | async as apps">
					<ng-container *ngIf="matchingIds$ | async as matchIds">

						<article class="lm__card" *ngFor="let p of patterns; trackBy: trackById" [class.lm__card--match]="matchIds.has(p.id)">
							<!-- HEADER -->
							<div class="lm__cardHeader lm__cardHeader--row">
								<div class="lm__cardTitleWrap">
									<h3 class="lm__cardTitle">{{ p.title }}</h3>

									<!-- MATCH BADGE -->
									<span class="lm__tag lm__tag--match" *ngIf="matchIds.has(p.id)">
										Matches
									</span>

									<!-- STATUS -->
									<button class="lm__pillBtn" [class.lm__pillBtn--active]="p.status === 1" [class.lm__pillBtn--inactive]="p.status !== 1" type="button" (click)="toggleStatus(p)">
										{{ p.status === 1 ? 'active' : 'inactive' }}
									</button>
								</div>

								<div class="lm__cardHeaderActions">

									<button class="lm__btn lm__btn--sm" type="button" (click)="refreshAllArticles(p)" [disabled]="!hasPreviewArticles(p) || (loading$ | async)">
										Refresh articles
									</button>

									<button class="lm__btn lm__btn--sm" type="button" (click)="test(p)">
										Test
									</button>

									<button class="lm__btn lm__btn--sm" type="button" (click)="showPreview(p)" [disabled]="!hasPreviewArticles(p)">
										Show preview
									</button>

									<button class="lm__btn lm__btn--sm" type="button" (click)="edit(p)">
										Edit
									</button>

									<button class="lm__btn lm__btn--sm lm__btn--danger" type="button" (click)="confirmDelete(p)" [disabled]="loading$ | async">
										Delete
									</button>
								</div>
							</div>

							<!-- BODY -->
							<div class="lm__cardBody">
								<pre class="lm__patternText">{{ p.pattern }}</pre>

								<div class="lm__muted" *ngIf="p.description" style="margin-top: 6px; white-space: pre-wrap;">
									{{ p.description }}
								</div>

								<!-- TAGS -->
								<div class="lm__tags" style="margin-top: 8px;">
									<span class="lm__tag" *ngIf="p.category">
										{{ p.category }}
									</span>

									<span class="lm__tag" *ngIf="p.jobLogType !== null && p.jobLogType !== undefined">
										Type: {{ jobLogTypeLabel(p.jobLogType) }}
									</span>

									<span class="lm__tag" *ngIf="p.fromApplicationId">
										From: {{ appNameById(apps, p.fromApplicationId) }}
									</span>

									<span class="lm__tag" *ngIf="p.toApplicationId">
										To: {{ appNameById(apps, p.toApplicationId) }}
									</span>

									<span class="lm__tag" *ngIf="p.linkTypeId">
										LinkType: {{ p.linkTypeId }}
									</span>

									<span class="lm__tag" *ngIf="p.searchInStackTrace">
										Stacktrace
									</span>

									<span class="lm__tag" *ngIf="(p.freshdeskArticleRowIds?.length ?? 0) > 0">
										Articles:
										{{ (p.freshdeskArticleRowIds ?? []).join(', ') }}
									</span>
								</div>

								<!-- META -->
								<div class="lm__metaRow">
									<div *ngIf="p.createdByName">
										<b>Created by:</b> {{ userLabel(p.createdByName) }}
									</div>
									<div>
										<b>Created:</b> {{ formatUtc(p.createdUtc) }}
									</div>
									<div *ngIf="p.updatedByName">
										<b>Last edited by:</b> {{ userLabel(p.updatedByName) }}
									</div>
									<div *ngIf="p.updatedUtc">
										<b>Updated:</b> {{ p.updatedUtc ? formatUtc(p.updatedUtc) : '—' }}
									</div>
								</div>

							</div>
						</article>

					</ng-container>
				</ng-container>
			</section>
		</ng-container>

		<!-- EDIT MODE (unchanged from your original) -->
		<ng-container *ngIf="mode === 'edit'">
			<div class="lm__pageHeader">
				<div>
					<h1>{{ editingId ? 'Edit Pattern' : 'New Pattern' }}</h1>
					<p>Store a pattern and optional constraints (filters).</p>
				</div>

				<div class="lm__headerActions">
					<button class="lm__btn" type="button" (click)="cancel()" [disabled]="loading$ | async">
						Cancel
					</button>

					<button class="lm__btn lm__btn--primary" type="button" (click)="save()" [disabled]="loading$ | async">
						{{ (loading$ | async) ? 'Saving…' : 'Save' }}
					</button>
				</div>
			</div>

			<div class="lm__error" *ngIf="(error$ | async) as err">
				<span *ngIf="err">{{ err }}</span>
			</div>

			<article class="lm__card">
				<div class="lm__cardBody">
					<div class="lm__formGrid">

						<label class="lm__label" style="grid-column: 1 / -1;">
							<span class="lm__labelTitle">Title</span>
							<input class="lm__input" type="text" [(ngModel)]="form.title" />
						</label>

						<label class="lm__label" style="grid-column: 1 / -1;">
							<span class="lm__labelTitle">
								Pattern <span class="lm__hint">Use &lt;VAR&gt;</span>
							</span>
							<textarea class="lm__textarea" rows="6" [(ngModel)]="form.pattern"></textarea>
						</label>

						<label class="lm__headerToggle" style="grid-column: 1 / -1;">
							<span class="lm__labelTitle">Search in stack trace</span>
							<span class="lm__switch lm__switch--sm">
								<input id="searchInStackTrace" type="checkbox" [(ngModel)]="form.searchInStackTrace" />
								<span class="lm__slider" aria-hidden="true"></span>
							</span>
						</label>

						<label class="lm__label" style="grid-column: 1 / -1;">
							<span class="lm__labelTitle">Description</span>
							<textarea class="lm__textarea" rows="6" [(ngModel)]="form.description"
								placeholder="Explain what this pattern is for..."></textarea>
						</label>

						<label class="lm__label" style="grid-column: 1 / -1;">
							<span class="lm__labelTitle">Display Message</span>
							<textarea class="lm__textarea" rows="6" [(ngModel)]="form.displayMessage"
								placeholder="Initial message to display to the user..."></textarea>
						</label>

						<label class="lm__label" style="grid-column: 1 / -1;">
							<span class="lm__labelTitle">Article ID(s)</span>
							<input class="lm__input" type="text" [(ngModel)]="articleIdsText"
								placeholder="e.g. 123, 456" />
							<span class="lm__hint">
								Comma / space separated. Only numbers are kept.
							</span>
						</label>
						<label class="lm__label">
							<span class="lm__labelTitle">Status</span>
							<select class="lm__input" [(ngModel)]="form.status">
								<option *ngFor="let s of statusOptions" [ngValue]="s.value">
									{{ s.label }}
								</option>
							</select>
						</label>

						<label class="lm__label">
							<span class="lm__labelTitle">Category</span>
							<input class="lm__input" type="text" [(ngModel)]="form.category" placeholder="(optional)" />
						</label>

						<label class="lm__label">
							<span class="lm__labelTitle">Job log type</span>
							<select class="lm__input" [(ngModel)]="form.jobLogType">
								<option *ngFor="let t of jobLogTypeOptions" [ngValue]="t.value">
									{{ t.label }}
								</option>
							</select>
						</label>

						<label class="lm__label">
							<span class="lm__labelTitle">From application</span>
							<select class="lm__input" [(ngModel)]="form.fromApplicationId">
								<option [ngValue]="null">All</option>
								<option *ngFor="let a of (applications$ | async) ?? []" [ngValue]="a.id">
									{{ a.displayName || a.name }}
								</option>
							</select>
						</label>

						<label class="lm__label">
							<span class="lm__labelTitle">To application</span>
							<select class="lm__input" [(ngModel)]="form.toApplicationId">
								<option [ngValue]="null">All</option>
								<option *ngFor="let a of (applications$ | async) ?? []" [ngValue]="a.id">
									{{ a.displayName || a.name }}
								</option>
							</select>
						</label>

						<label class="lm__label">
							<span class="lm__labelTitle">LinkTypeId</span>
							<input class="lm__input" type="number" [(ngModel)]="form.linkTypeId" placeholder="Any" />
						</label>
					</div>
				</div>
			</article>
		</ng-container>
	</main>
</div>

<al-freshdesk-help-modal #helpModal [canRefreshArticles]="true"></al-freshdesk-help-modal>
